import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { ACCENT, formatMXN, CustomTooltip } from "../cuentas/shared";
import { getMonthRange, formatCalendarDate } from "../../utils/dates";
import { detalleMensual } from "../../utils/reportes";
import { useSerieAnual, SelectorAnio, COSTO_COLOR } from "./shared";

// FLUJO DE CAJA: el dinero que de hecho entró y salió cada mes, venga del
// evento que venga — un anticipo de abril puede llegar desde junio del año
// anterior, y aquí cuenta en el mes en que llegó. La rentabilidad de los
// eventos es otro reporte (Estado de resultados): son preguntas distintas
// y viven separadas a propósito.
const ReporteFlujoCaja = ({ depositos: depositosProp = null }) => {
  const navigate = useNavigate();
  const { anio, years, setAnio, serie, tz, events, payments, refunds, costos, depositos, retiros } =
    useSerieAnual(depositosProp);

  const totales = useMemo(
    () =>
      serie.reduce(
        (acc, m) => ({
          cobrado: acc.cobrado + m.cobrado,
          reembolsos: acc.reembolsos + m.reembolsos,
          costos: acc.costos + m.costos,
          depositosNeto: acc.depositosNeto + m.depositosEntrada - m.depositosSalida,
          retiros: acc.retiros + m.retiros,
          flujoNeto: acc.flujoNeto + m.flujoNeto,
        }),
        { cobrado: 0, reembolsos: 0, costos: 0, depositosNeto: 0, retiros: 0, flujoNeto: 0 }
      ),
    [serie]
  );

  // Drill-down: los pagos, reembolsos, costos pagados y depósitos del mes.
  const [mesAbierto, setMesAbierto] = useState(null);
  const detalle = useMemo(() => {
    if (!mesAbierto) return null;
    const { from, to } = getMonthRange(mesAbierto);
    return detalleMensual({
      payments,
      refunds,
      costos,
      depositos,
      retiros,
      events,
      desde: from,
      hasta: to,
      timeZone: tz,
    });
  }, [mesAbierto, payments, refunds, costos, depositos, retiros, events, tz]);

  const conDatos = serie.some(
    (m) =>
      m.cobrado > 0 ||
      m.costos > 0 ||
      m.reembolsos > 0 ||
      m.depositosEntrada > 0 ||
      m.depositosSalida > 0 ||
      m.retiros > 0
  );
  const celda = (v, cls = "text-zinc-600 dark:text-zinc-300") =>
    v !== 0 ? <span className={cls}>{formatMXN(v)}</span> : <span className="text-zinc-300 dark:text-zinc-600">—</span>;

  return (
    <div className="space-y-6">
      <SelectorAnio anio={anio} years={years} setAnio={setAnio} />

      {!conDatos ? (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-10 text-center text-sm text-zinc-400 shadow-sm">
          Sin movimientos de caja en {anio}.
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-zinc-50 dark:bg-zinc-800/40">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Mes</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Ingresos</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Reembolsos</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Egresos</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Depósitos</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Retiros</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Flujo neto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {serie.map((m) => {
                    const abierto = mesAbierto === m.key;
                    return (
                      <React.Fragment key={m.key}>
                        <tr
                          onClick={() => setMesAbierto(abierto ? null : m.key)}
                          className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition"
                          title="Ver los movimientos del mes"
                        >
                          <td className="px-4 py-2 text-zinc-900 dark:text-white font-medium whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <ChevronRightIcon
                                className={`h-4 w-4 text-zinc-400 transition-transform ${abierto ? "rotate-90" : ""}`}
                              />
                              {m.label}
                              {m.futuro && (
                                <span className="ml-1 text-[10px] uppercase tracking-wide text-zinc-400">
                                  por venir
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">{celda(m.cobrado)}</td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">{celda(m.reembolsos, "text-red-600")}</td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">{celda(m.costos, "text-red-600")}</td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {celda(
                              m.depositosEntrada - m.depositosSalida,
                              m.depositosEntrada - m.depositosSalida < 0
                                ? "text-red-600"
                                : "text-amber-600"
                            )}
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">{celda(m.retiros, "text-zinc-500")}</td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {m.cobrado !== 0 || m.costos !== 0 || m.reembolsos !== 0 || m.depositosEntrada !== 0 || m.depositosSalida !== 0 || m.retiros !== 0
                              ? celda(m.flujoNeto, m.flujoNeto < 0 ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold")
                              : <span className="text-zinc-300 dark:text-zinc-600">—</span>}
                          </td>
                        </tr>
                        {abierto && detalle && (
                          <tr>
                            <td colSpan={7} className="px-5 py-4 bg-zinc-50/70 dark:bg-zinc-800/30">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-left">
                                <div>
                                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                                    Ingresos · {formatMXN(m.cobrado)}
                                    {m.reembolsos > 0 ? ` (− ${formatMXN(m.reembolsos)} reembolsados)` : ""}
                                  </div>
                                  {detalle.pagos.length === 0 && detalle.reembolsos.length === 0 ? (
                                    <p className="text-xs text-zinc-400">Sin pagos este mes.</p>
                                  ) : (
                                    <div className="space-y-1">
                                      {detalle.pagos.map((p) => (
                                        <div key={p.id} className="flex items-baseline gap-2 text-xs">
                                          <span className="text-zinc-400 w-16 shrink-0">
                                            {formatCalendarDate(p.fecha, tz, { style: "short" })}
                                          </span>
                                          {p.eventId ? (
                                            <button
                                              type="button"
                                              onClick={() => navigate(`/dashboard/eventos/${p.eventId}`)}
                                              className="truncate hover:underline text-left"
                                              style={{ color: ACCENT }}
                                            >
                                              {p.eventNombre || "Evento"}
                                            </button>
                                          ) : (
                                            <span className="text-zinc-500 truncate">Pago suelto</span>
                                          )}
                                          {p.metodo && (
                                            <span className="text-zinc-400 hidden sm:inline">· {p.metodo}</span>
                                          )}
                                          <span className="ml-auto font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                                            {formatMXN(p.monto)}
                                          </span>
                                        </div>
                                      ))}
                                      {detalle.reembolsos.map((r) => (
                                        <div key={r.id} className="flex items-baseline gap-2 text-xs">
                                          <span className="text-zinc-400 w-16 shrink-0">
                                            {formatCalendarDate(r.fecha, tz, { style: "short" })}
                                          </span>
                                          <span className="text-red-600 truncate">
                                            Reembolso{r.eventNombre ? ` · ${r.eventNombre}` : ""}
                                          </span>
                                          <span className="ml-auto font-medium text-red-600 whitespace-nowrap">
                                            −{formatMXN(r.monto)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                                    Egresos · {formatMXN(m.costos)}
                                  </div>
                                  {detalle.costosCaja.length === 0 ? (
                                    <p className="text-xs text-zinc-400">Sin egresos este mes.</p>
                                  ) : (
                                    <div className="space-y-1">
                                      {detalle.costosCaja.map((c) => (
                                        <div key={c.id} className="flex items-baseline gap-2 text-xs">
                                          <span className="text-zinc-400 w-16 shrink-0">
                                            {formatCalendarDate(c.fecha, tz, { style: "short" })}
                                          </span>
                                          <span className="text-zinc-700 dark:text-zinc-300 truncate">
                                            {c.concepto}
                                            {c.categoria ? (
                                              <span className="text-zinc-400"> · {c.categoria}</span>
                                            ) : null}
                                            {c.metodo ? (
                                              <span className="text-zinc-400"> · {c.metodo}</span>
                                            ) : null}
                                          </span>
                                          {c.eventId && (
                                            <button
                                              type="button"
                                              onClick={() => navigate(`/dashboard/eventos/${c.eventId}`)}
                                              className="truncate hover:underline hidden sm:inline text-left"
                                              style={{ color: ACCENT }}
                                            >
                                              {c.eventNombre || "Evento"}
                                            </button>
                                          )}
                                          <span
                                            className={`ml-auto font-medium whitespace-nowrap ${
                                              c.tipo === "devolucion" ? "text-emerald-600" : "text-red-600"
                                            }`}
                                          >
                                            {formatMXN(c.monto)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {detalle.depositos.length > 0 && (
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                                      Depósitos en garantía
                                    </div>
                                    <div className="space-y-1">
                                      {detalle.depositos.map((dep) => (
                                        <div key={dep.id} className="flex items-baseline gap-2 text-xs">
                                          <span className="text-zinc-400 w-16 shrink-0">
                                            {formatCalendarDate(dep.fecha, tz, { style: "short" })}
                                          </span>
                                          <span className="text-zinc-700 dark:text-zinc-300 truncate">
                                            {dep.aplicado
                                              ? dep.parcial ? "Aplicado (parcial)" : "Aplicado"
                                              : dep.monto >= 0 ? "Recibido" : dep.parcial ? "Devuelto (parcial)" : "Devuelto"}
                                            {dep.detalle ? (
                                              <span className="text-zinc-400"> · {dep.detalle.toLowerCase()}</span>
                                            ) : null}
                                            {dep.proveedorNombre ? (
                                              <span className="text-zinc-700 dark:text-zinc-300"> · {dep.proveedorNombre}</span>
                                            ) : null}
                                            {dep.eventNombre ? (
                                              <span className="text-zinc-400"> · {dep.eventNombre}</span>
                                            ) : null}
                                          </span>
                                          {dep.aplicado ? (
                                            // Se queda el venue: no mueve caja, pasa de custodia a ingreso.
                                            <span
                                              className="ml-auto font-medium whitespace-nowrap text-zinc-500"
                                              title="El venue se quedó este monto: no mueve caja, pasa de custodia a ingreso"
                                            >
                                              {formatMXN(dep.montoAplicado)} · ingreso
                                            </span>
                                          ) : (
                                            <span
                                              className={`ml-auto font-medium whitespace-nowrap ${
                                                dep.monto >= 0 ? "text-amber-600" : "text-red-600"
                                              }`}
                                            >
                                              {dep.monto >= 0 ? "+" : "−"}{formatMXN(Math.abs(dep.monto))}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {detalle.retiros.length > 0 && (
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                                      Retiros · {formatMXN(m.retiros)}
                                    </div>
                                    <div className="space-y-1">
                                      {detalle.retiros.map((r) => (
                                        <div key={r.id} className="flex items-baseline gap-2 text-xs">
                                          <span className="text-zinc-400 w-16 shrink-0">
                                            {formatCalendarDate(r.fecha, tz, { style: "short" })}
                                          </span>
                                          <span className="text-zinc-700 dark:text-zinc-300 truncate">
                                            {r.concepto}
                                            {r.metodo ? (
                                              <span className="text-zinc-400"> · {r.metodo}</span>
                                            ) : null}
                                          </span>
                                          <span className="ml-auto font-medium text-zinc-600 dark:text-zinc-300 whitespace-nowrap">
                                            −{formatMXN(r.monto)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <p className="mt-3 text-xs text-zinc-400">
                                Solo movimientos de caja: cada egreso cuenta el
                                día que se pagó y lo por pagar vive en Cuentas → Por pagar.
                                Los retiros salen de la cuenta pero no son
                                gasto: no tocan ningún resultado.
                              </p>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="bg-zinc-50 dark:bg-zinc-800/40 border-t border-zinc-200 dark:border-zinc-700/60">
                  <tr>
                    <td className="px-4 py-2.5 font-semibold text-zinc-900 dark:text-white">Total {anio}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-zinc-900 dark:text-white whitespace-nowrap">{formatMXN(totales.cobrado)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-red-600 whitespace-nowrap">{formatMXN(totales.reembolsos)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-red-600 whitespace-nowrap">{formatMXN(totales.costos)}</td>
                    <td className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${totales.depositosNeto < 0 ? "text-red-600" : "text-amber-600"}`}>
                      {formatMXN(totales.depositosNeto)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-zinc-600 dark:text-zinc-300 whitespace-nowrap">
                      {formatMXN(totales.retiros)}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${totales.flujoNeto < 0 ? "text-red-600" : "text-emerald-600"}`}>
                      {formatMXN(totales.flujoNeto)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={serie} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    width={48}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="cobrado" name="Ingresos" fill={ACCENT} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="costos" name="Egresos" fill={COSTO_COLOR} radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="flujoNeto" name="Flujo neto" stroke="#52525b" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <p className="text-xs text-zinc-400">
            Cada movimiento cuenta en el mes en que el dinero se movió, venga
            del evento que venga: los egresos entran con su fecha de pago y lo
            por pagar no aparece hasta pagarse. Los depósitos en garantía
            cuentan al recibirse y al devolverse; aplicarlos no mueve caja.
            Los retiros del dueño bajan el neto de caja sin ser gasto.
            Estas cifras pueden diferir de versiones anteriores del reporte,
            que contaba costos aún no pagados. La rentabilidad por evento vive
            en el Estado de resultados.
          </p>
        </>
      )}
    </div>
  );
};

export default ReporteFlujoCaja;
